import React from 'react';
import { BsCheckCircleFill } from 'react-icons/bs';
import { FaCrown } from 'react-icons/fa';

const Filters = ({ filterState, setFilterState, allNumbers }) => {
    const { network: activeNetwork, category: activeCategory } = filterState;

    const handleNetworkClick = (netName) => {
        // Toggle off if already active
        setFilterState({ ...filterState, network: activeNetwork === netName ? null : netName });
    };

    const handleCategoryClick = (catName) => {
        // Toggle off if already active
        setFilterState({ ...filterState, category: activeCategory === catName ? null : catName });
    };

    const getNetworkCount = (networkName) => {
        if (!allNumbers || allNumbers.length === 0) return "Loading...";
        const count = allNumbers.filter(n => n.network?.toLowerCase() === networkName.toLowerCase()).length;
        return `${count} Numbers`;
    };

    const networks = [
        { name: 'Dialog', subtitle: getNetworkCount('Dialog'), color: 'text-red-500', activeBg: 'bg-red-50', activeBorder: 'border-red-500', logo: '/network_logos/dialog.png' },
        { name: 'Mobitel', subtitle: getNetworkCount('Mobitel'), color: 'text-green-600', activeBg: 'bg-white', activeBorder: 'border-green-600', logo: '/network_logos/mobitel.png' },
        { name: 'Hutch', subtitle: getNetworkCount('Hutch'), color: 'text-orange-500', activeBg: 'bg-white', activeBorder: 'border-orange-500', logo: '/network_logos/hutch.png' },
        { name: 'Airtel', subtitle: getNetworkCount('Airtel'), color: 'text-red-600', activeBg: 'bg-white', activeBorder: 'border-red-600', logo: '/network_logos/airtel.png' }
    ];

    const categories = [
        { name: 'Bronze', subtitle: 'Below 10,000 LKR', iconColor: 'text-amber-700', activeBorder: 'border-amber-700' },
        { name: 'Silver', subtitle: '10,000 - 25,000 LKR', iconColor: 'text-gray-400', activeBorder: 'border-gray-400' },
        { name: 'Gold', subtitle: '25,000 - 50,000 LKR', iconColor: 'text-yellow-500', activeBorder: 'border-yellow-500' },
        { name: 'Platinum', subtitle: '50,000 - 100,000 LKR', iconColor: 'text-slate-400', activeBorder: 'border-slate-400' },
        { name: 'VIP', subtitle: 'Above 100,000 LKR', iconColor: 'text-red-600', activeBorder: 'border-red-600', isVip: true }
    ];

    return (
        <section className="py-5 bg-white">
            <div className="container-fluid mx-auto" style={{ maxWidth: '95%' }}>
                
                {/* Choose Network */}
                <div className="flex flex-col xl:flex-row items-start xl:items-center mb-6">
                    <h3 
                        className="font-bold text-lg xl:text-xl w-48 mb-4 xl:mb-0 flex-shrink-0"
                        style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#333' }}
                    >
                        Choose Network
                    </h3>
                    {/* Mobile/Tablet View (Grid) */}
                    <div className="grid grid-cols-2 gap-2 w-full lg:hidden">
                        {networks.map((net) => (
                            <button 
                                key={net.name}
                                onClick={() => handleNetworkClick(net.name)}
                                className={`flex flex-col items-center justify-center p-2 rounded-xl border transition-all bg-white relative
                                    ${activeNetwork === net.name 
                                        ? `border-red-600 ring-1 ring-red-600 bg-red-50 shadow-[0_0_12px_rgba(220,38,38,0.5)] z-10` 
                                        : 'border-gray-200 hover:border-gray-300 shadow-sm'}`}
                            >
                                <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center mb-1">
                                    <img src={net.logo} alt={net.name} className="max-w-full max-h-full object-contain" />
                                </div>
                                <div className="text-center">
                                    <div className={`font-extrabold text-sm ${activeNetwork === net.name ? 'text-red-600' : 'text-gray-900'}`}>
                                        {net.name}
                                    </div>
                                    <div className="text-[10px] font-medium text-gray-500 mt-0.5">{net.subtitle}</div>
                                </div>
                                {activeNetwork === net.name && (
                                    <BsCheckCircleFill className="text-red-600 absolute top-2 right-2" size={14} />
                                )}
                            </button>
                        ))}
                    </div>

                    {/* Desktop View (Original) */}
                    <div className="hidden lg:flex flex-wrap gap-4 w-full">
                        {networks.map((net) => (
                            <button 
                                key={net.name}
                                onClick={() => handleNetworkClick(net.name)}
                                className={`flex items-center justify-between px-4 py-3 rounded-xl border flex-1 min-w-[200px] transition-all bg-white
                                    ${activeNetwork === net.name 
                                        ? `border-red-600 ring-1 ring-red-600 bg-red-50 shadow-[0_0_16px_rgba(220,38,38,0.7)] relative z-10` 
                                        : 'border-gray-200 hover:border-gray-300 shadow-sm'}`}
                            >
                                <div className="flex items-center space-x-3">
                                    <div className="w-14 h-14 flex-shrink-0 flex items-center justify-center">
                                        <img src={net.logo} alt={net.name} className="max-w-full max-h-full object-contain" />
                                    </div>
                                    <div className="text-left border-l pl-3 ml-2 border-gray-200">
                                        <div className={`font-extrabold text-base ${activeNetwork === net.name ? 'text-red-600' : 'text-gray-900'}`}>
                                            {net.name}
                                        </div>
                                        <div className="text-xs font-medium text-gray-500 mt-0.5">{net.subtitle}</div>
                                    </div>
                                </div>
                                {activeNetwork === net.name && (
                                    <BsCheckCircleFill className="text-red-600 ml-2" size={20} />
                                )}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Choose Category */}
                <div className="flex flex-col xl:flex-row items-start xl:items-center">
                    <h3 
                        className="font-bold text-lg xl:text-xl w-48 mb-4 xl:mb-0 flex-shrink-0"
                        style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#333' }}
                    >
                        Choose Category
                    </h3>
                    {/* Mobile/Tablet View (Grid) */}
                    <div className="grid grid-cols-2 gap-2 w-full lg:hidden">
                        {categories.map((cat) => (
                            <button 
                                key={cat.name}
                                onClick={() => handleCategoryClick(cat.name)}
                                className={`flex flex-col items-center justify-center p-2 rounded-xl border transition-all bg-white relative
                                    ${activeCategory === cat.name 
                                        ? `border-red-600 ring-1 ring-red-600 bg-red-50 shadow-[0_0_12px_rgba(220,38,38,0.5)] z-10` 
                                        : 'border-gray-200 hover:border-gray-300 shadow-sm'}`}
                            >
                                <div className={`mb-1 relative flex items-center justify-center w-8 h-8 rounded-md bg-gradient-to-br from-gray-100 to-gray-200 border border-gray-300 shadow-inner flex-shrink-0 ${cat.iconColor}`}>
                                    <FaCrown size={14} />
                                </div>
                                <div className="text-center">
                                    <div className={`font-extrabold text-sm ${cat.isVip ? 'text-red-600' : 'text-gray-900'}`}>
                                        {cat.name}
                                    </div>
                                    <div className="text-xs font-medium text-gray-600 mt-0.5">{cat.subtitle}</div>
                                </div>
                                {activeCategory === cat.name && (
                                    <BsCheckCircleFill className="text-red-600 absolute top-2 right-2" size={12} />
                                )}
                            </button>
                        ))}
                    </div>

                    {/* Desktop View (Original) */}
                    <div className="hidden lg:flex flex-wrap gap-4 w-full">
                        {categories.map((cat) => (
                            <button 
                                key={cat.name}
                                onClick={() => handleCategoryClick(cat.name)}
                                className={`flex items-center px-4 py-3 rounded-xl border flex-1 min-w-[200px] transition-all bg-white
                                    ${activeCategory === cat.name 
                                        ? `border-red-600 ring-1 ring-red-600 bg-red-50 shadow-[0_0_16px_rgba(220,38,38,0.7)] relative z-10` 
                                        : 'border-gray-200 hover:border-gray-300 shadow-sm'}`}
                            >
                                <div className="flex items-center">
                                    <div className={`mr-4 relative flex items-center justify-center w-10 h-10 rounded-md bg-gradient-to-br from-gray-100 to-gray-200 border border-gray-300 shadow-inner flex-shrink-0 ${cat.iconColor}`}>
                                        <FaCrown size={16} />
                                    </div>
                                    <div className="text-left">
                                        <div className={`font-extrabold text-base ${cat.isVip ? 'text-red-600' : 'text-gray-900'}`}>
                                            {cat.name}
                                        </div>
                                        <div className="text-sm font-medium text-gray-600 mt-0.5">{cat.subtitle}</div>
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>

            </div>
        </section>
    );
};

export default Filters;
